import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { StandardToolResult } from '../../../types/standardToolResults';
import { CommandExecutionContext } from '../../../types/commands';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';
import { schemaToParameters } from '../../../utils/schemaToParameters';

export const email_read: SimpleDocCommand = {
  id: 'email_read',
  logicalName: 'email_read',
  version: 'v1',

  description: `Search and read emails from connected accounts (Gmail, Outlook, IMAP).

**FILTERS:**
- query: Full-text search across subject, body, and sender
- from: Sender email address
- subject: Email subject line
- folder: Folder/label (INBOX, Sent, Drafts, etc.)
- startDate/endDate: Date range in ISO 8601 format (YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS)
- isRead: Filter by read status (true = read, false = unread)
- limit: Max emails to return (default 20, max 50)

**EXAMPLES:**

Read recent emails from a specific sender:
{ "from": "boss@company.com", "startDate": "2026-02-01", "limit": 30 }

Search for unread emails in inbox:
{ "folder": "INBOX", "isRead": false }

Search by subject keyword:
{ "query": "contract renewal", "limit": 10 }

**RETURNS:** Array of emails with subject, from, date, body (truncated), folder, isRead`,

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        query:     { type: 'string',  description: 'Full-text search query across subject, body, and sender' },
        from:      { type: 'string',  description: 'Filter by sender email address' },
        subject:   { type: 'string',  description: 'Filter by email subject' },
        folder:    { type: 'string',  description: 'Filter by folder/label (INBOX, Sent, Drafts, etc.)' },
        startDate: { type: 'string',  description: 'Filter by start date (ISO 8601: YYYY-MM-DD or YYYY-MM-DDTHH:MM:SS)' },
        endDate:   { type: 'string',  description: 'Filter by end date (ISO 8601 format)' },
        isRead:    { type: 'boolean', description: 'Filter by read status (true = read, false = unread)' },
        limit:     { type: 'number',  description: 'Max emails to return (default 20, max 50)' },
      },
      required: [],
    }),
  },

  formatViewData: (result: any, toolInput: any) => {
    try {
      if (result?.status === 'success' && result?.data) {
        const data = result.data;
        return {
          type: 'email_results',
          summary: result.message || `Found ${data.count || 0} emails`,
          data: {
            emails: data.emails || [],
            count: data.count || 0,
            total_count: data.total_count || data.count || 0,
            truncated: data.truncated || false,
            account: data.account,
            query: toolInput?.query
          },
          meta: {
            searchParams: {
              query: toolInput?.query,
              from: toolInput?.from,
              subject: toolInput?.subject,
              folder: toolInput?.folder
            }
          }
        };
      }
      return {
        type: 'tool_summary',
        summary: result?.message || 'Email search completed',
        data: result?.data || {}
      };
    } catch (error) {
      console.warn('[email_read] Error in formatViewData:', error);
      return {
        type: 'tool_summary',
        summary: 'Email search completed',
        data: {}
      };
    }
  },

  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const { query, from, subject, folder, startDate, endDate, isRead, limit = 20, from_account } = params;

    try {
      const numericLimit = typeof limit === 'number' && !isNaN(limit) ? limit : 20;
      const safeLimit = Math.min(Math.max(1, numericLimit), 50);

      const buildBody = (integrationId?: string) => {
        const body: any = {};
        if (query) body.query = query;
        if (from) body.from = from;
        if (subject) body.subject = subject;
        if (folder) body.folder = folder;
        if (startDate) body.start_date = startDate;
        if (endDate) body.end_date = endDate;
        if (isRead !== undefined) body.is_read = isRead;
        body.limit = safeLimit;
        if (integrationId) body.integration_id = integrationId;
        return body;
      };

      // If a specific account is requested, resolve its integration ID first
      if (from_account) {
        const entitiesResp = await fetchService(`${API_BASE_URL}/entities`, { method: 'GET' });
        const match = (entitiesResp || []).find(
          (e: any) =>
            (e.entityType === 'Integration.Gmail' ||
              (e.entityType === 'Integration' && e.skeleton?.integrationType === 'Gmail')) &&
            e.skeleton?.connectionData?.accountId === from_account &&
            e.skeleton?.status === 'connected'
        );
        const data = await fetchService(`${API_BASE_URL}/email/search`, {
          method: 'POST',
          body: JSON.stringify(buildBody(match?._id)),
        });
        const emails = Array.isArray(data) ? data : data.emails || [];
        return {
          status: 'success',
          message: `Found ${emails.length} email${emails.length !== 1 ? 's' : ''} in ${from_account}`,
          data: { emails, count: emails.length, account: from_account, truncated: emails.length >= safeLimit },
          format: 'json',
        };
      }

      // No account specified — search all connected accounts
      const data = await fetchService(`${API_BASE_URL}/email/search`, {
        method: 'POST',
        body: JSON.stringify(buildBody()),
      });

      const emails = Array.isArray(data) ? data : data.emails || [];

      if (!emails || emails.length === 0) {
        return {
          status: 'success',
          message: 'No emails found matching criteria',
          data: { emails: [], count: 0 },
          format: 'json',
        };
      }

      return {
        status: 'success',
        message: `Found ${emails.length} email${emails.length > 1 ? 's' : ''}`,
        data: { emails, count: emails.length, truncated: emails.length >= safeLimit },
        format: 'json',
      };
    } catch (error: any) {
      console.error('[email_read] Error:', error);

      let errorMessage = 'Failed to search emails';
      let errorDetails = error.message;
      if (error.data?.detail) {
        errorDetails = error.data.detail;
        errorMessage = errorDetails;
      }

      const errorCategory =
        error.status === 400 ? 'validation' :
        error.status === 403 ? 'permission' :
        error.status === 404 ? 'validation' : 'execution';

      return {
        status: 'error',
        message: errorMessage,
        error: {
          code: error.status ? `http_${error.status}` : 'unknown',
          category: errorCategory as 'execution' | 'validation' | 'permission',
          details: errorDetails,
        },
        format: 'plaintext',
      };
    }
  }
};
